export function getNotificationUnreadCount(data = {}) {
  const unreadCount = Number(data?.unreadCount || 0);
  return Number.isFinite(unreadCount) && unreadCount > 0 ? unreadCount : 0;
}

function decodePathSegment(value = '') {
  try {
    return decodeURIComponent(value);
  } catch (err) {
    return value;
  }
}

function getActionSearchParams(actionUrl = '') {
  const queryStart = actionUrl.indexOf('?');
  if (queryStart === -1) return new URLSearchParams();
  return new URLSearchParams(actionUrl.slice(queryStart + 1));
}

export function isMessageNotificationActionUrl(actionUrl = '') {
  return actionUrl.startsWith('/messages/') || actionUrl.startsWith('/messages?');
}

export function normalizeMessageNotification(notification = {}) {
  const actionUrl = typeof notification?.actionUrl === 'string' ? notification.actionUrl.trim() : '';
  if (!isMessageNotificationActionUrl(actionUrl)) return null;

  const unreadCount = getNotificationUnreadCount(notification);
  if (unreadCount <= 0) return null;

  if (actionUrl.startsWith('/messages/')) {
    const pathTail = actionUrl.slice('/messages/'.length).split(/[?#]/)[0] || '';
    return {
      type: 'direct',
      unreadCount,
      threadId: notification.threadId || decodePathSegment(pathTail),
    };
  }

  const params = getActionSearchParams(actionUrl);
  const teamId = notification.teamId || params.get('teamId') || '';
  const boardId = notification.boardId || params.get('boardId') || '';
  const topicId = notification.topicId || params.get('topicId') || '';
  const moderationCaseId = notification.moderationCaseId || params.get('caseId') || '';

  return {
    type: 'boards',
    unreadCount,
    teamId,
    boardId,
    topicId,
    moderationCaseId,
  };
}

export function boardNotificationKey(teamId = '', boardId = '', topicId = '') {
  return [teamId, boardId, topicId].filter(Boolean).join(':');
}

export function summarizeMessageNotifications(notifications = []) {
  return notifications.reduce((summary, notification) => {
    const normalized = normalizeMessageNotification(notification);
    if (!normalized) return summary;

    summary.total += normalized.unreadCount;

    if (normalized.type === 'direct') {
      summary.direct.total += normalized.unreadCount;
      if (normalized.threadId) {
        summary.direct.byThread[normalized.threadId] = (summary.direct.byThread[normalized.threadId] || 0) + normalized.unreadCount;
      }
      return summary;
    }

    summary.boards.total += normalized.unreadCount;
    if (normalized.teamId && normalized.boardId) {
      const boardKey = boardNotificationKey(normalized.teamId, normalized.boardId);
      summary.boards.byBoard[boardKey] = (summary.boards.byBoard[boardKey] || 0) + normalized.unreadCount;
    }
    if (normalized.teamId && normalized.boardId && normalized.topicId) {
      const topicKey = boardNotificationKey(normalized.teamId, normalized.boardId, normalized.topicId);
      summary.boards.byTopic[topicKey] = (summary.boards.byTopic[topicKey] || 0) + normalized.unreadCount;
    }
    if (normalized.teamId && normalized.boardId && normalized.moderationCaseId) {
      const caseKey = boardNotificationKey(normalized.teamId, normalized.boardId, normalized.moderationCaseId);
      summary.boards.byCase[caseKey] = (summary.boards.byCase[caseKey] || 0) + normalized.unreadCount;
    }
    return summary;
  }, {
    total: 0,
    direct: { total: 0, byThread: {} },
    boards: { total: 0, byBoard: {}, byTopic: {}, byCase: {} },
  });
}

export function isSelectedBoardNotification(notification = {}, selection = {}) {
  const normalized = normalizeMessageNotification(notification);
  if (!normalized || normalized.type !== 'boards') return false;

  if (selection.caseId) {
    return Boolean(
      normalized.moderationCaseId
      && normalized.moderationCaseId === selection.caseId
      && (!selection.teamId || normalized.teamId === selection.teamId)
      && (!selection.boardId || normalized.boardId === selection.boardId)
    );
  }

  if (!selection.teamId || !selection.boardId || !selection.topicId) return false;
  return normalized.teamId === selection.teamId
    && normalized.boardId === selection.boardId
    && normalized.topicId === selection.topicId;
}
